import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import React, { useEffect } from "react";
import Product from "../../../../../../utilities/data/model/Product";
import { useTheme } from "@towersystems/roam-common-native";
import { toTitleCase } from "../../../../../../utilities/to-title-case";
import {
  useAddItem,
  useSaleControllerProvider,
} from "../../../../utils/sale-controller";
import { QuickKey as BaseQuickKey, QuickKeyProps } from "../../../QuickKey";
import { GIFTCARD_PRODUCT_SKU } from "../../../../../../utilities/gift-card/constants";

export type ProductQuickKeyProps = QuickKeyProps & {
  product: Product;
  onProductAdded?: () => void;
  onVariantPickerOpen?: () => void;
  canTriggerEdit?: boolean;
};

export const ProductQuickKey = React.memo(
  React.forwardRef<any, ProductQuickKeyProps>(
    (
      {
        product,
        onProductAdded,
        onVariantPickerOpen,
        disabled: _disabled,
        backgroundImage, // icon image url will be used instead
        backgroundColor,
        labelColor,
        label,
        ...rest
      },
      ref
    ) => {
      const { sale } = useSaleControllerProvider();

      const [canModifySale, setCanModifySale] = React.useState(!sale);

      const { colors: Colors } = useTheme();

      const navigation = useNavigation();
      const addItem = useAddItem({ navigation });

      React.useEffect(() => {
        (async () => {
          if (sale) {
            const r = await sale.canModifySale();
            setCanModifySale(r);
          } else {
            setCanModifySale(true);
          }
        })();
      }, [sale]);

      function addItemToSale(item: Product) {
        if (item?.hasVariants) {
          //@ts-ignore
          navigation.navigate("VariantPickerModalScreen", {
            productId: item.id,
          });
          return onVariantPickerOpen && onVariantPickerOpen();
        }

        return addItem(item.id)
          .then(onProductAdded)
          .catch((e) => console.log(e));
      }

      const subtitle = product?.hasVariants
        ? `${product.totalVariants} variations`
        : product?.variantParentId
        ? product?.displayName
        : undefined;

      let icon = null;

      const textColor = labelColor
        ? labelColor
        : product?.imageUrl
        ? Colors.white
        : backgroundImage
        ? Colors.text
        : undefined;

      if (product?.hasVariants) {
        icon = <AntDesign name="tagso" size={24} color={textColor} />;
      } else if (product.serialNumbersSale) {
        icon = <AntDesign name="barcode" size={24} color={textColor} />;
      }

      if (product.stockCode == GIFTCARD_PRODUCT_SKU) {
        icon = (
          <MaterialIcons name="card-giftcard" size={24} color={textColor} />
        );
      } else if (!!product.isKit) {
        icon = (
          <MaterialIcons
            name="shopping-basket"
            size={24}
            color={Colors.white}
          />
        );
      }

      return (
        <BaseQuickKey
          ref={ref}
          onPress={() => {
            product && addItemToSale(product);
          }}
          disabled={
            _disabled ?? (canModifySale == false || product.active === false)
          }
          isDestructive={false}
          isAction={false}
          label={label ? label : toTitleCase(product?.name || "")}
          subtitle={subtitle}
          backgroundImage={product?.imageUrl}
          labelStyle={{
            color: textColor,
          }}
          iconPrimary={icon}
          iconImageUrl={backgroundImage}
          backgroundColor={backgroundColor}
          {...rest}
        />
      );
    }
  ),
  (p: any, p2: any) => {
    return p.product?.id === p2.product?.id;
  }
);
